/**
 * Shared mobile Mapbox marker interaction helpers.
 *
 * Goal: keep Open Houses + All Listings mobile map interactions consistent:
 * - tap marker
 * - pan with a consistent vertical offset (so the popup area stays visible under the header)
 * - add popup AFTER pan completes (moveend)
 *
 * This intentionally does not know about listing/open-house domain objects.
 */

/**
 * Compute a Mapbox `easeTo` offsetY value that places the marker at a consistent
 * screen position relative to the top header on mobile.
 *
 * @param {HTMLElement|null} mapContainerEl
 * @param {{ estimatedHeaderHeight?: number, popupAboveMarkerHeight?: number, topBuffer?: number }} opts
 * @returns {number|null} offsetY suitable for `easeTo({ offset: [0, offsetY] })` or null if container missing
 */
export function computeMobileMarkerOffsetY(
  mapContainerEl,
  { estimatedHeaderHeight = 90, popupAboveMarkerHeight = 0, topBuffer = -10 } = {}
) {
  if (!mapContainerEl) return null;
  const viewportHeight = mapContainerEl.clientHeight;
  const targetMarkerScreenY = estimatedHeaderHeight + popupAboveMarkerHeight + topBuffer;
  return targetMarkerScreenY - viewportHeight / 2;
}

/**
 * Compute a duration for the mobile pan based on how far the camera needs to move.
 * This keeps the perceived motion speed consistent across different pan distances.
 *
 * @param {import('mapbox-gl').Map} map
 * @param {{ lon: number, lat: number }} lngLat
 * @param {{ msPerPixel?: number, minMs?: number, maxMs?: number }} opts
 * @returns {number}
 */
export function computeMobilePanDurationMs(
  map,
  lngLat,
  { msPerPixel = 1.35, minMs = 600, maxMs = 1300 } = {}
) {
  if (!map) return 500;
  try {
    const from = map.project(map.getCenter());
    const to = map.project([lngLat.lon, lngLat.lat]);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const ms = Math.round(dist * msPerPixel);
    return Math.max(minMs, Math.min(maxMs, ms));
  } catch {
    return 500;
  }
}

/**
 * Pan the map to a marker and add the popup after the pan completes.
 *
 * @param {object} args
 * @param {import('mapbox-gl').Map} args.map
 * @param {import('mapbox-gl').Popup} args.popup
 * @param {{ lon: number, lat: number }} args.lngLat
 * @param {HTMLElement|null} args.mapContainerEl
 * @param {{ current: any }} args.originalMapViewRef - ref used to restore camera after popup close
 * @param {() => void} args.addPopup - callback invoked after pan completes to add popup + attach listeners
 * @param {{ duration?: number, fallbackOffsetY?: number, msPerPixel?: number, minMs?: number, maxMs?: number }} [args.opts]
 */
export function panToMarkerAndAddPopupOnMoveEnd({
  map,
  popup,
  lngLat,
  mapContainerEl,
  originalMapViewRef,
  addPopup,
  opts = {}
}) {
  const { duration, fallbackOffsetY = -150, msPerPixel, minMs, maxMs } = opts;
  const resolvedDuration = typeof duration === 'number'
    ? duration
    : computeMobilePanDurationMs(map, lngLat, { msPerPixel, minMs, maxMs });

  if (!map) return;

  // Save the view so we can restore when popup closes.
  originalMapViewRef.current = {
    center: map.getCenter(),
    zoom: map.getZoom()
  };

  const addPopupAfterPan = () => {
    if (!map) return;
    if (!popup.isOpen()) addPopup();
    map.off('moveend', addPopupAfterPan);
  };

  map.once('moveend', addPopupAfterPan);

  const calculatedOffsetY = computeMobileMarkerOffsetY(mapContainerEl);
  map.easeTo({
    center: [lngLat.lon, lngLat.lat],
    offset: [0, calculatedOffsetY ?? fallbackOffsetY],
    duration: resolvedDuration,
    essential: true
  });
}


